// auth.guard.ts (class-based)
import { Injectable } from "@angular/core";
import {
  CanActivate,
  CanActivateChild,
  CanLoad,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  Route,
  UrlSegment
} from "@angular/router";
import { AuthService } from "../services/auth.service";

@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
  constructor(private router: Router, private authService: AuthService) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    return this.checkAuthentication(state.url);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    return this.checkAuthentication(state.url);
  }

  canLoad(route: Route, segments: UrlSegment[]): boolean {
    const url = '/' + segments.map(segment => segment.path).join('/');
    return this.checkAuthentication(url);
  }

  private checkAuthentication(url: string): boolean {
    console.log("AuthGuard: Checking authentication for URL:", url);
    
    // Define public routes that don't require authentication
    const publicRoutes = [
      '/login',
      '/login-service-provider',
      '/otp-verification',
      '/country-selector',
      '/company/auth/login',
      '/company/auth/forget-password',
      '/company/auth/otp-verification',
      '/public/company-registration',
      '/public/influencer-registration',
      '/logout'
    ];

    // Check if user is authenticated using the enhanced AuthService
    const isAuthenticated = this.authService.isAuthenticated();
    console.log("AuthGuard: User authenticated:", isAuthenticated);
    
    if (isAuthenticated) {
      // If authenticated, check if user is trying to access auth pages
      // and redirect to appropriate dashboard
      const authRoutes = ['/login', '/login-service-provider', '/otp-verification', '/country-selector'];
      const companyAuthRoutes = ['/company/auth/login', '/company/auth/forget-password', '/company/auth/otp-verification'];
      
      if (authRoutes.includes(url) || companyAuthRoutes.some(route => url.startsWith(route))) {
        console.log("AuthGuard: Authenticated user accessing auth page, redirecting to dashboard");
        this.authService.redirectToDefaultRoute();
        return false;
      }
  
      console.log("AuthGuard: Authentication check passed");
      return true;
    }
    
    // Check if the current URL is a public route
    const isPublicRoute = publicRoutes.some(route => url.startsWith(route));
    
    if (isPublicRoute) {
      console.log("AuthGuard: Public route, access granted");
      return true;
    }
    console.log("AuthGuard: User not authenticated, redirecting to login");
    // Route to correct login based on attempted area
    if (url.startsWith('/company') || url.startsWith('/financial-advisor')) {
      this.router.navigate(["/login-service-provider"]);
    } else {
      // Remember where the customer was going; Quick Login returns them there.
      this.router.navigate(["/login"], { queryParams: { returnUrl: url } });
    }
    return false;

  }
}
